import conf from 'conf/conf.js';

const DEFAULT_LOCAL_CODE = 'UAH';
const DEFAULT_LOCAL_SYMBOL = '₴';

export const isCurrencyOnce = (): boolean => Boolean(conf.currencyOnce);

export const isEuroMode = (): boolean => Boolean(conf.euroMode);

/** Local (country) currency code from ENV. */
export const getCurrencyCode = (): string => {
    const code = String(conf.currencyCode || conf.currentCode || DEFAULT_LOCAL_CODE).trim();
    return code ? code.toUpperCase() : DEFAULT_LOCAL_CODE;
};

/** Local (country) currency symbol from ENV. */
export const getCurrencySymbol = (): string => {
    const symbol = String(conf.currencySymbol || '').trim();
    return symbol || DEFAULT_LOCAL_SYMBOL;
};

/** Human-readable local currency name (e.g. ГРН, злотий). Falls back to code. */
export const getCurrencyName = (): string => {
    const name = String(conf.currencyName || '').trim();
    return name || getCurrencyCode();
};

export const getLocalCurrencyPrefix = (): string => `${getCurrencySymbol()} `;

/**
 * Base dual-currency slot: USD by default, EUR when REACT_APP_EURO_MODE=true.
 * Visual only — architecture stays on "USD" fields.
 */
export const getBaseCurrencyCode = (): 'USD' | 'EUR' => (isEuroMode() ? 'EUR' : 'USD');

export const getBaseCurrencySymbol = (): string => (isEuroMode() ? '€' : '$');

export const getBaseCurrencyPrefix = (): string => `${getBaseCurrencySymbol()} `;

/** Params for i18n interpolation in currency-related copy. */
export const getCurrencyI18nParams = () => ({
    baseCode: getBaseCurrencyCode(),
    baseSymbol: getBaseCurrencySymbol(),
    localCode: getCurrencyCode(),
    localSymbol: getCurrencySymbol(),
    localName: getCurrencyName(),
    code: getBaseCurrencyCode(),
    symbol: getBaseCurrencySymbol(),
});

/** Single-currency / local-only mode (historically rate === 1). */
export const getIsLocalCurrencyMode = (rate: number): boolean =>
    (Number(rate ?? 1) || 1) === 1;

/** @deprecated use getIsLocalCurrencyMode */
export const getIsUahMode = getIsLocalCurrencyMode;

/** Display helpers — `amount` is already formatted or raw display value. */
export const withLocalCurrency = (amount: string | number, negative = false): string =>
    negative
        ? `-${getCurrencySymbol()} ${amount}`
        : `${getLocalCurrencyPrefix()}${amount}`;

export const withBaseCurrency = (amount: string | number, negative = false): string =>
    negative
        ? `-${getBaseCurrencySymbol()} ${amount}`
        : `${getBaseCurrencyPrefix()}${amount}`;

export const withPrimaryCurrency = (
    amount: string | number,
    rate: number,
    negative = false,
): string =>
    getIsLocalCurrencyMode(rate)
        ? withLocalCurrency(amount, negative)
        : withBaseCurrency(amount, negative);
